import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Paper,
  Box,
  Grid,
  Typography,
  Container,
} from '@mui/material';
import Footer from './components/Footer';
import { getPlatformBasedTitle } from '../utils/titleUtils';
import { Helmet } from 'react-helmet';

// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const WebsiteLayoutWS = ({
  children,
  businessData,
  value,
  handleChange,
  full,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const mainRef = useRef(null);

  const [langMenuAnchorEl, setLangMenuAnchorEl] = useState(null);

  const currentType = localStorage.getItem('type');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Enhanced scroll to top effect
  useEffect(() => {
    const scrollToTop = () => {
      // Try multiple methods to ensure scrolling works
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // If we have a ref, try scrolling that as well
      if (mainRef.current) {
        mainRef.current.scrollTop = 0;
      }
    };

    // Execute immediately
    scrollToTop();

    // Also try after a small delay to ensure DOM is ready
    const timeoutId = setTimeout(scrollToTop, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Open language menu
  const handleLangMenuOpen = (event) => {
    setLangMenuAnchorEl(event.currentTarget);
  };

  // Close language menu
  const handleLangMenuClose = () => {
    setLangMenuAnchorEl(null);
  };

  const navigateHome = () => {
    navigate('/');
  };


  const handleLogin = () => {
    if (currentType === 'customer') {
      window.open('https://portal.usenode.com', '_blank');
    } else {
      window.open('https://chekup.ca/signin', '_blank');
    }
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const activeButtonStyle = {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -5,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '30px',
      height: '3px',
      backgroundColor: '#bc0c0c',
      borderRadius: '2px',
    },
    fontWeight: 700,
  };

  return (
    <Box
      ref={mainRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        position: 'relative',
        background: '#ffffff',
        height: '100%',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Helmet>
        <title>{getPlatformBasedTitle()}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <ScrollToTop />
      <AppBar
        sx={{ zIndex: 200, backgroundColor: '#FFFFFF98' }}
        elevation={0}
        position="fixed"
      >
        <Toolbar className="justify-content-between" sx={{ py: 1.5 }}>
          <div onClick={navigateHome} className="hover">
            <img
              src="/assets/chekup/chekup-logo.png"
              alt="logo"
              className="mt-1"
              style={{
                height: '30px',
                marginRight: '13px',
              }}
            />
          </div>
          <div>
            <Button
              sx={{
                fontWeight: isActive('/platform') ? 700 : 600,
                marginLeft: '7px',
                fontSize: '0.9em',
                marginRight: '7px',
                color: theme?.palette?.mode === 'dark' ? '#FFF' : '#000',
                ...(isActive('/platform') && activeButtonStyle),
              }}
              onClick={() => navigate('/platform')}
              variant="text"
            >
              {t('platform')}
            </Button>

            <Button
              sx={{
                fontWeight: isActive('/approch') ? 700 : 600,
                marginLeft: '7px',
                fontSize: '0.9em',
                marginRight: '7px',
                color: theme?.palette?.mode === 'dark' ? '#FFF' : '#000',
                ...(isActive('/approch') && activeButtonStyle),
              }}
              onClick={() => navigate('/approch')}
              variant="text"
            >
              {t('approch')}
            </Button>

            {!isMobile && (
              <Button
                sx={{
                  fontWeight: isActive('/use-cases') ? 700 : 600,
                  marginLeft: '7px',
                  fontSize: '0.9em',
                  marginRight: '7px',
                  color: theme?.palette?.mode === 'dark' ? '#FFF' : '#000',
                  ...(isActive('/use-cases') && activeButtonStyle),
                }}
                onClick={() => navigate('/use-cases')}
                variant="text"
              >
                {t('useCases')}
              </Button>
            )}
            {!isMobile && (
              <Button
                sx={{
                  fontWeight: isActive('/meet-chekup') ? 700 : 500,
                  marginLeft: '45px',
                  marginRight: '7px',
                  fontSize: '0.9em',
                  color: theme?.palette?.mode === 'dark' ? '#FFF' : '#000',
                  ...(isActive('/meet-chekup') && activeButtonStyle),
                }}
                onClick={() => navigate('/meet-chekup')}
                variant="text"
              >
                {t('planDemo')}
              </Button>
            )}
          </div>
          <div>
            <Button
              aria-controls="language-menu"
              aria-haspopup="true"
              onClick={handleLangMenuOpen}
              variant="text"
              sx={{
                fontSize: '0.9em',
                color: theme?.palette?.mode === 'dark' ? '#FFF' : '#000',
              }}
            >
              {localStorage.getItem('i18nextLng') || 'fr'}
            </Button>
            <Menu
              id="language-menu"
              anchorEl={langMenuAnchorEl}
              keepMounted
              open={Boolean(langMenuAnchorEl)}
              onClose={handleLangMenuClose}
            >
              <MenuItem onClick={() => changeLanguage('en')}>EN</MenuItem>
              <MenuItem onClick={() => changeLanguage('fr')}>FR</MenuItem>
            </Menu>

            {!isMobile && (
              <Button
                sx={{
                  fontSize: '0.9em',
                  marginLeft: '7px',
                  borderRadius: '40px',
                  color: '#ffffff',
                  borderColor: isActive('/signin') ? '#bc0c0c' : '#000',
                  backgroundColor: isActive('/signin') ? '#bc0c0c' : '#000',
                  fontWeight: isActive('/signin') ? 700 : 400,
                  boxShadow: isActive('/signin')
                    ? '0px 4px 10px rgba(188, 12, 12, 0.25)'
                    : 'none',
                }}
                onClick={handleLogin}
                variant="outlined"
                color="primary"
              >
                {t('login')}
              </Button>
            )}
          </div>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          pt: '74px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {children}
      </Box>

      <Footer />
    </Box>
  );
};

export default WebsiteLayoutWS;
